import { carbonConvert } from "../utils/metrics";
import { CarbonMeasurementSystem, CarbonUnit } from "../utils/units/units";
import { FieldInputProps, useField } from "formik";
import { roundToNPlaces } from "common/utils/numbers";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React, { ChangeEvent, ReactNode, useCallback } from "react";

interface LocalUnitFieldProps {
  name: string;
  storedUnit: CarbonUnit;
  displayUnits: {
    [system in CarbonMeasurementSystem]: CarbonUnit;
  };
  children: (
    props: FieldInputProps<number> & { displayUnit: string }
  ) => ReactNode;
  /**
   * because of unit system conversions, integer input values get turned into
   * messily non-integral internal representations and lose precision,
   * introducing rounding errors.  to increase user confidence, this helper
   * rounds off the last decimal places of the displayed value, in the thought
   * that the user was unlikely to have entered e.g. an implement width of
   * exactly 34.00000706 feet.  the appropriate number to choose here depends
   * on the magnitude of the value you expect to be entered; the default (4)
   * should work well for values between ~1 and ~1000, but you should consider
   * decreasing it by 1 for every order of magnitude above that, or increasing
   * it if you're expecting a user to enter a value small enough that 0.0001 is
   * a significant fraction.
   */
  roundDisplayToNPlaces?: number;
}

export const LocalUnitField: React.FC<LocalUnitFieldProps> = ({
  name,
  storedUnit,
  displayUnits,
  children,
  roundDisplayToNPlaces = 4,
}) => {
  const { t } = useTranslation();
  const localSystem = useSelf().measurementSystem;
  const [field, meta, helpers] = useField(name);

  const { value: storedValue } = meta;
  const displayUnit = displayUnits[localSystem];
  const rawDisplayValue = carbonConvert(storedValue, storedUnit, displayUnit);
  const formattedDisplayValue = roundToNPlaces(
    rawDisplayValue,
    roundDisplayToNPlaces
  );

  const handleChange = useCallback(
    (e: ChangeEvent<any>) => {
      const value: number = Number.parseFloat(e.target.value);

      // work around https://github.com/jaredpalmer/formik/issues/1191
      // and https://github.com/jaredpalmer/formik/issues/2083
      helpers
        .setValue(carbonConvert(value, displayUnit, storedUnit))
        .finally(() => helpers.setTouched(true));
    },
    [displayUnit, storedUnit, helpers]
  );

  return children({
    ...field,
    value: formattedDisplayValue,
    onChange: handleChange,
    // carbon.actions.compareKeys.ignoreDynamic
    displayUnit: t(`utils.units.${displayUnit}`),
  });
};
